/**
 * Profil / tverrsnitt: tegn en linje A → B (to klikk) med justerbar bredde → sidevisning av alle punkter i korridoren.
 *
 * - Punktene hentes i full oppløsning fra COPC for korridoren (alle nivåer som krysser den), uavhengig av 3D-LOD,
 *   og filtreres i workerne. Grove nivåer kommer først, så profilen fylles ut gradvis.
 * - Metrisk rutenett: avstand fra A (m) og høyde (m i prosjektets høydesystem), zoom/panorering, hover-avlesning,
 *   måling av høyde/avstand, og vektorobjekter (ledninger, master, minsteavstand …) når lagene er slått på.
 * - I Klassifisering kan punkter velges i profilen (rektangel eller enkeltpunkt) og få ny klasse – samme regler og
 *   revisjon som i 3D; kildefilen endres aldri.
 * Tegning: punktene rasteriseres til en pikselbuffer (rask for millioner av punkter); tegnes bare ved endring.
 */
import * as THREE from 'three';
import { el } from '../format';
import type { Engine, ProfileProgress } from '../scene/engine';
import type { ClassificationSession } from '../classification/session';
import type { OverlayManager } from '../layers/overlays';
import { KIND_COLOR } from '../layers/overlays';
import { classInfo, elevationRamp } from '../scene/classes';
import { encodeBits, type ClassEdit } from '../classification/manual';
import {
  drawOrder, fitViewport, nearestPicked, niceTicks, panViewport, profileLength, profileRing, rasterizeProfile, robustRange,
  toProfile, toScreen, zoomViewport, type ProfileSection, type ProfileViewport,
} from '../classification/profile';
import { applyCorrections, ProfileData, selectionMask } from '../classification/profileData';
import { profileVectors, type ProfileVector } from '../layers/profileVectors';
import type { XY } from '../gis/geometry';

type Tool = 'pan' | 'measure' | 'select';
type ColorMode = 'class' | 'rgb' | 'intensity' | 'elevation';
const nb = (v: number, d = 1) => v.toLocaleString('nb-NO', { minimumFractionDigits: d, maximumFractionDigits: d });
const count = (v: number) => Math.round(v).toLocaleString('nb-NO');
/** ABGR (little-endian RGBA i Uint32) */
const abgr = (r: number, g: number, b: number) => ((255 << 24) | (b << 16) | (g << 8) | r) >>> 0;
const hexToAbgr = (hex: string) => { const v = parseInt(hex.slice(1), 16); return abgr((v >> 16) & 255, (v >> 8) & 255, v & 255); };
const SELECTED = abgr(255, 153, 0);
const MARGIN = { left: 58, right: 12, top: 18, bottom: 24 };
const STRIDE_LIMIT = 1_200_000;

export interface ProfileHooks {
  /** etter at en korreksjon er brukt (oppdater klassifiseringspanelet) */
  redraw: () => void;
  /** start nytt valg av A/B i kartet */
  repick: () => void;
}

export class ProfileView {
  readonly root = el('section', { class: 'profile-window hidden', 'aria-label': 'Profil / tverrsnitt' });
  private canvas = el('canvas', { class: 'profile-canvas', 'aria-label': 'Profil: avstand fra A (m) og høyde (m)', tabIndex: 0 });
  private raster = document.createElement('canvas');
  private readout = el('span', { class: 'profile-readout', 'aria-live': 'off' });
  private progressEl = el('span', { class: 'profile-progress', 'aria-live': 'polite' });
  private meta = el('span', { class: 'profile-meta' });
  private info = el('div', { class: 'note', 'aria-live': 'polite' });
  private width = el('input', { type: 'number', value: '3', min: 0.1, max: 1000, step: 0.5, 'aria-label': 'Profilbredde i meter', title: 'Korridorbredde (m) – punkter innenfor ± halv bredde tas med' });
  private exSelect = el('select', { 'aria-label': 'Vertikal overhøyde', title: 'Vertikal overhøyde' }, ...[['auto', 'Auto'], ['1', '1×'], ['2', '2×'], ['5', '5×'], ['10', '10×']].map(([v, t]) => el('option', { value: v }, t)));
  private colorSelect = el('select', { 'aria-label': 'Profilfarge', title: 'Farge i profilen' }, ...[['class', 'Klasse'], ['rgb', 'RGB'], ['intensity', 'Intensitet'], ['elevation', 'Høyde']].map(([v, t]) => el('option', { value: v }, t)));
  private toolButtons = new Map<Tool, HTMLButtonElement>();
  private classifyPanel: HTMLElement;
  private group = new THREE.Group();

  private section: ProfileSection | null = null;
  private data = new ProfileData();
  private view: ProfileViewport | null = null;
  private exaggeration: number | null = null;
  private colorMode: ColorMode = 'class';
  private tool: Tool = 'pan';
  private controller: AbortController | null = null;
  private progress: ProfileProgress | null = null;
  private loading = false;
  private userMoved = false;
  private classifying = false;
  private storage: string;
  private vertical: string;

  // tegnebuffere
  private image: ImageData | null = null;
  private pixels: Uint32Array | null = null;
  private pick: Int32Array | null = null;
  private colors = new Uint32Array(0);
  private order: ArrayLike<number> = new Uint32Array(0);
  private orderVersion = -1;
  private colorKey = '';
  private rasterKey = '';
  private raf = 0;
  private fastUntil = 0;
  private fastTimer: number | null = null;
  private vectors: ProfileVector[] = [];
  private vectorKey = '';
  private editsKey = '';

  // interaksjon
  private drag: { id: number; x: number; y: number; x0: number; y0: number; mode: 'pan' | 'select'; moved: boolean } | null = null;
  private hover: XY | null = null;
  private measure: XY[] = [];
  private selection: XY[] | null = null;
  private pending: ClassEdit | null = null;
  private selMask: Uint8Array | null = null;
  private selVersion = 0;

  // klassifisering
  private sources = el('select', { multiple: true, size: 5, 'aria-label': 'Profil kildeklasser' }, ...Array.from({ length: 256 }, (_, i) => el('option', { value: String(i), selected: [3, 4, 5].includes(i) }, `${i} · ${classInfo(i).name}`)));
  private target = el('select', { 'aria-label': 'Profil målklasse' }, ...Array.from({ length: 256 }, (_, i) => el('option', { value: String(i), selected: i === 14 }, `${i} · ${classInfo(i).name}`)));
  private reason = el('input', { value: 'Manuell profil-QC', 'aria-label': 'Profil begrunnelse', maxLength: 2000 });
  private apply = el('button', { disabled: true, class: 'primary' }, 'Bruk profilklasse');

  constructor(private engine: Engine, private session: ClassificationSession, private overlays: OverlayManager, private hooks: ProfileHooks) {
    this.storage = 'gk-profile:' + engine.cfg.apiBase + ':' + engine.cfg.projectId;
    this.vertical = /5941/.test(session.vertical) ? 'NN2000' : session.vertical && session.vertical !== 'UNKNOWN' ? session.vertical : 'prosjektets høydesystem';
    this.group.renderOrder = 45;
    engine.scene.add(this.group);

    const tool = (id: Tool, label: string, title: string) => {
      const b = el('button', { type: 'button', class: 'seg-btn', 'aria-pressed': 'false', title, onclick: () => this.setTool(id) }, label) as HTMLButtonElement;
      this.toolButtons.set(id, b);
      return b;
    };
    const resize = el('div', { class: 'profile-resize', role: 'separator', 'aria-orientation': 'horizontal', 'aria-label': 'Endre høyde på profilvinduet', title: 'Dra for å endre høyden' });
    const head = el('div', { class: 'profile-head' },
      el('b', { class: 'profile-title' }, 'Profil A → B'), this.meta,
      el('label', { class: 'profile-field' }, 'Bredde', this.width, 'm'),
      el('div', { class: 'seg', role: 'group', 'aria-label': 'Profilverktøy' }, tool('pan', 'Panorer', 'Dra for å panorere · hjul zoomer · Shift+hjul = bare høyde'), tool('measure', 'Mål', 'Klikk to punkter: avstand, høydeforskjell og skrå avstand'), tool('select', 'Velg', 'Dra et rektangel eller klikk et punkt for omklassifisering')),
      el('label', { class: 'profile-field' }, 'Overhøyde', this.exSelect),
      el('label', { class: 'profile-field' }, 'Farge', this.colorSelect),
      el('div', { class: 'profile-actions' },
        el('button', { type: 'button', title: 'Tilpass visningen (F)', onclick: () => { this.userMoved = false; this.fit(); } }, 'Tilpass'),
        el('button', { type: 'button', title: 'Flytt profilen én bredde mot venstre (sett fra A mot B)', 'aria-label': 'Flytt profil mot venstre', onclick: () => this.shift(1) }, '◀'),
        el('button', { type: 'button', title: 'Flytt profilen én bredde mot høyre', 'aria-label': 'Flytt profil mot høyre', onclick: () => this.shift(-1) }, '▶'),
        el('button', { type: 'button', title: 'Bytt retning (B → A)', onclick: () => this.reverse() }, 'Snu'),
        el('button', { type: 'button', title: 'Tegn en ny profillinje i kartet', onclick: () => hooks.repick() }, 'Ny profil'),
        el('button', { type: 'button', class: 'profile-close', title: 'Lukk profil', 'aria-label': 'Lukk profil', onclick: () => this.close() }, '✕')));
    this.classifyPanel = this.buildClassifyPanel();
    const plot = el('div', { class: 'profile-plot' }, this.canvas);
    this.root.append(resize, head, el('div', { class: 'profile-content' }, plot, this.classifyPanel), el('div', { class: 'profile-status' }, this.readout, this.progressEl));
    this.setTool('pan');
    this.setClassify(false);

    // høyde på vinduet (huskes)
    try { const h = Number(localStorage.getItem('gk-profile-height')); if (h >= 180) this.root.style.height = `${h}px`; } catch { /* bare bekvemmelighet */ }
    resize.addEventListener('pointerdown', (e) => {
      const startY = e.clientY, startH = this.root.getBoundingClientRect().height, max = (this.root.parentElement?.clientHeight ?? 800) - 60;
      resize.setPointerCapture(e.pointerId);
      const move = (ev: PointerEvent) => { this.root.style.height = `${Math.round(Math.max(180, Math.min(max, startH + startY - ev.clientY)))}px`; };
      const up = () => { resize.removeEventListener('pointermove', move); resize.removeEventListener('pointerup', up); try { localStorage.setItem('gk-profile-height', String(Math.round(this.root.getBoundingClientRect().height))); } catch { /* */ } };
      resize.addEventListener('pointermove', move);
      resize.addEventListener('pointerup', up);
    });

    let widthTimer: number | null = null;
    const applyWidth = () => {
      const w = Number(this.width.value);
      if (!this.section || !Number.isFinite(w) || w < 0.1 || w > 1000) { this.info.textContent = 'Bredde må være 0,1–1000 m.'; return; }
      if (Math.abs(w - this.section.width) > 1e-9) this.open({ ...this.section, width: w }, false);
    };
    this.width.addEventListener('change', applyWidth);
    this.width.addEventListener('input', () => { if (widthTimer !== null) clearTimeout(widthTimer); widthTimer = window.setTimeout(applyWidth, 450); });
    this.exSelect.addEventListener('change', () => { this.exaggeration = this.exSelect.value === 'auto' ? null : Number(this.exSelect.value); this.fit(); });
    this.colorSelect.addEventListener('change', () => { this.colorMode = this.colorSelect.value as ColorMode; this.schedule(); });

    new ResizeObserver(() => { if (!this.isOpen) return; if (!this.userMoved) this.fit(); else this.schedule(); }).observe(plot);
    this.bindPointer();
  }

  get isOpen(): boolean { return !this.root.classList.contains('hidden') && !!this.section; }
  attach(stage: HTMLElement) { stage.append(this.root); }
  /** Velg-verktøy og klassifiseringspanel bare i Klassifisering. */
  setClassify(on: boolean) {
    this.classifying = on;
    this.classifyPanel.classList.toggle('hidden', !on);
    this.toolButtons.get('select')!.classList.toggle('hidden', !on);
    if (!on && this.tool === 'select') this.setTool('pan');
    if (!on) this.invalidate();
    this.schedule();
  }

  saved(): ProfileSection | null {
    try {
      const p = JSON.parse(localStorage.getItem(this.storage) ?? 'null');
      if (p && p.a?.length === 2 && p.b?.length === 2 && [...p.a, ...p.b, p.width].every(Number.isFinite) && profileLength(p) >= 0.1 && p.width >= 0.1 && p.width <= 1000) return { ...p, selection: [] };
    } catch { /* */ }
    return null;
  }

  open(section: ProfileSection, refit = true) {
    this.section = structuredClone(section);
    this.width.value = String(section.width);
    this.root.classList.remove('hidden');
    try { localStorage.setItem(this.storage, JSON.stringify(section)); } catch { /* */ }
    this.invalidate();
    this.measure = [];
    this.data.clear();
    this.vectorKey = '';
    this.rasterKey = '';
    if (refit) { this.userMoved = false; this.view = null; }
    this.meta.textContent = `${nb(profileLength(section), 1)} m · ${this.vertical}`;
    this.drawCorridor();
    this.load();
    this.schedule();
  }

  close() {
    this.root.classList.add('hidden');
    this.controller?.abort();
    this.controller = null;
    this.loading = false;
    this.section = null;
    this.data.clear();
    this.invalidate();
    this.measure = [];
    this.clearCorridor();
  }

  // ------------------------------------------------------------------ data
  private load() {
    const section = this.section;
    if (!section) return;
    this.controller?.abort();
    const controller = this.controller = new AbortController(), started = performance.now();
    this.loading = true;
    this.progress = null;
    this.progressEl.textContent = 'Finner COPC-noder i korridoren …';
    let firstFit = false;
    void this.engine.fetchProfile({ a: section.a, b: section.b, width: section.width }, controller.signal,
      (chunk) => {
        if (this.controller !== controller) return;
        this.data.add(chunk);
        this.editsKey = '';
        if (!firstFit && !this.userMoved && this.data.count > 2000) { firstFit = true; this.fit(); }
        this.schedule();
      },
      (p) => { if (this.controller === controller) { this.progress = p; this.updateProgress(false, started); } },
    ).then((p) => {
      if (this.controller !== controller) return;
      this.loading = false;
      this.progress = p;
      this.updateProgress(true, started);
      if (!this.userMoved) this.fit();
      this.drawCorridor();
      this.schedule();
    }).catch((e) => {
      if (this.controller !== controller) return;
      this.loading = false;
      this.progressEl.textContent = 'Profilhenting feilet: ' + (e as Error).message;
    });
  }

  private updateProgress(done: boolean, started: number) {
    const p = this.progress;
    if (!p) return;
    const parts = done
      ? [`${count(this.data.count)} punkter i korridoren · ${p.nodes} COPC-noder · ${nb((performance.now() - started) / 1000, 1)} s`]
      : [`Henter fulloppløst profil · ${p.done}/${p.nodes} noder · ${count(this.data.count)} punkter`];
    if (p.capped) parts.push('begrenset: fineste nivå utelatt (over 3 mill. punkter) – smalere bredde gir full oppløsning');
    if (p.failed) parts.push(`${p.failed} noder feilet – profilen er ufullstendig`);
    if (done && !p.nodes) parts.splice(0, 1, 'Ingen punktsky krysser korridoren (eller ingen synlige punktskyer).');
    this.progressEl.textContent = parts.join(' · ');
    this.progressEl.classList.toggle('warn', p.capped || p.failed > 0);
  }

  // ------------------------------------------------------------------ visning
  private plotRect(): ProfileViewport['plot'] {
    const w = this.canvas.clientWidth, h = this.canvas.clientHeight;
    return { x: MARGIN.left, y: MARGIN.top, w: Math.max(10, w - MARGIN.left - MARGIN.right), h: Math.max(10, h - MARGIN.top - MARGIN.bottom) };
  }

  private fit() {
    if (!this.section) return;
    const L = profileLength(this.section), z = robustRange(this.data.z.subarray(0, this.data.count)) ?? this.fallbackZ();
    const pad = Math.max(1, (z[1] - z[0]) * 0.04);
    this.view = fitViewport(this.plotRect(), [0, L], [z[0] - pad, z[1] + pad], this.exaggeration);
    this.schedule();
  }

  private fallbackZ(): [number, number] {
    const b = this.engine.projectBounds(true);
    return b ? [b.min[2], b.max[2]] : [0, 100];
  }

  private schedule() {
    if (this.raf || !this.isOpen) return;
    this.raf = requestAnimationFrame(() => { this.raf = 0; this.draw(); });
  }

  /** Under rask interaksjon med mange punkter tegnes et utvalg; full tegning ~150 ms etter siste bevegelse. */
  private interacting() {
    if (this.data.count <= STRIDE_LIMIT) return;
    this.fastUntil = performance.now() + 150;
    if (this.fastTimer !== null) clearTimeout(this.fastTimer);
    this.fastTimer = window.setTimeout(() => { this.fastTimer = null; this.schedule(); }, 170);
  }

  private hiddenKey(): string { return [...this.engine.style.hiddenClasses].sort((a, b) => a - b).join(','); }

  private updateColors() {
    const d = this.data, n = d.count;
    const key = `${d.version}|${this.colorMode}|${this.hiddenKey()}|${this.selVersion}`;
    if (key === this.colorKey && this.colors.length >= n) return;
    this.colorKey = key;
    if (this.colors.length < n) this.colors = new Uint32Array(Math.max(n, Math.ceil(this.colors.length * 1.5)));
    const hidden = this.engine.style.hiddenClasses, palette = new Uint32Array(256);
    for (let c = 0; c < 256; c++) palette[c] = hidden.has(c) ? 0 : hexToAbgr(classInfo(c).color);
    const mode = this.colorMode, mask = this.selMask && this.selMask.length === n ? this.selMask : null;
    let ramp: Uint8Array | null = null, z0 = 0, z1 = 1, i0 = 0, i1 = 65535;
    if (mode === 'elevation') { ramp = elevationRamp(); const r = robustRange(d.z.subarray(0, n), 0.01, 0.99) ?? [0, 1]; z0 = r[0]; z1 = Math.max(r[1], r[0] + 0.01); }
    if (mode === 'intensity') { const r = robustRange(d.intensity.subarray(0, n), 0.01, 0.99) ?? [0, 65535]; i0 = r[0]; i1 = Math.max(r[1], r[0] + 1); }
    for (let i = 0; i < n; i++) {
      const c = d.classes[i];
      if (!palette[c]) { this.colors[i] = 0; continue; }
      if (mask && mask[i]) { this.colors[i] = SELECTED; continue; }
      if (mode === 'class') this.colors[i] = palette[c];
      else if (mode === 'rgb') { const r = d.rgb[3 * i], g = d.rgb[3 * i + 1], b = d.rgb[3 * i + 2]; this.colors[i] = r + g + b > 0 ? abgr(r, g, b) : abgr(158, 163, 171); }
      else if (mode === 'intensity') { const v = Math.round(255 * Math.pow(Math.min(1, Math.max(0, (d.intensity[i] - i0) / (i1 - i0))), 0.7)); this.colors[i] = abgr(v, v, v); }
      else { const k = Math.round(255 * Math.min(1, Math.max(0, (d.z[i] - z0) / (z1 - z0)))) * 4; this.colors[i] = abgr(ramp![k], ramp![k + 1], ramp![k + 2]); }
    }
  }

  private syncCorrections() {
    const key = `${this.data.version}|${this.session.originalView}|${this.session.edits.map((e) => e.id).join(',')}`;
    if (key === this.editsKey || !this.section) return;
    applyCorrections(this.data, this.section, this.session.edits, this.session.originalView);
    this.editsKey = `${this.data.version}|${this.session.originalView}|${this.session.edits.map((e) => e.id).join(',')}`;
  }

  private syncVectors() {
    if (!this.section) return;
    const visible = this.overlays.layers.filter((l) => l.visible && l.status === 'ready' && l.parsed);
    const key = `${this.section.a}|${this.section.b}|${this.section.width}|` + visible.map((l) => `${l.def.id}:${l.parsed!.features.length}:${l.filter ?? ''}`).join(',');
    if (key === this.vectorKey) return;
    this.vectorKey = key;
    this.vectors = profileVectors(visible.map((l) => ({ id: l.def.id, kind: l.def.kind, features: this.overlays.features(l), baseColor: KIND_COLOR[l.def.kind] })), this.section);
  }

  private draw() {
    if (!this.isOpen || !this.section) return;
    const cssW = this.canvas.clientWidth, cssH = this.canvas.clientHeight;
    if (!cssW || !cssH) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2), W = Math.round(cssW * dpr), H = Math.round(cssH * dpr);
    if (this.canvas.width !== W || this.canvas.height !== H) { this.canvas.width = W; this.canvas.height = H; }
    if (!this.view) this.fit();
    const plot = this.plotRect();
    if (this.view!.plot.w !== plot.w || this.view!.plot.h !== plot.h || this.view!.plot.x !== plot.x) this.view = { ...this.view!, plot };
    const v = this.view!;
    this.syncCorrections();
    this.syncVectors();
    this.updateColors();
    if (this.orderVersion !== this.data.version) { this.order = drawOrder(this.data.classes, this.data.count); this.orderVersion = this.data.version; }

    // punkter → pikselbuffer (bare når noe som påvirker punktene er endret)
    const stride = performance.now() < this.fastUntil ? Math.ceil(this.data.count / STRIDE_LIMIT) : 1;
    const size = Math.max(1, Math.round((v.sx > 60 ? 3 : v.sx > 15 ? 2 : 1) * dpr));
    const key = `${W}x${H}|${v.cx}|${v.cz}|${v.sx}|${v.sz}|${this.colorKey}|${this.orderVersion}|${stride}|${size}`;
    if (key !== this.rasterKey) {
      this.rasterKey = key;
      if (!this.image || this.image.width !== W || this.image.height !== H) {
        this.image = new ImageData(W, H);
        this.pixels = new Uint32Array(this.image.data.buffer);
        this.pick = new Int32Array(W * H);
        this.raster.width = W;
        this.raster.height = H;
      }
      this.pixels!.fill(0);
      this.pick!.fill(-1);
      const dv: ProfileViewport = { cx: v.cx, cz: v.cz, sx: v.sx * dpr, sz: v.sz * dpr, plot: { x: v.plot.x * dpr, y: v.plot.y * dpr, w: v.plot.w * dpr, h: v.plot.h * dpr } };
      rasterizeProfile(this.pixels!, this.pick, W, H, dv, this.data.s, this.data.z, this.colors, this.order, size, stride);
      this.raster.getContext('2d')!.putImageData(this.image, 0, 0);
    }

    const ctx = this.canvas.getContext('2d')!;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    const css = getComputedStyle(this.root);
    ctx.fillStyle = css.getPropertyValue('--viewport').trim() || '#0e1419';
    ctx.fillRect(0, 0, W, H);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.drawGrid(ctx, v, cssW, cssH);
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.drawImage(this.raster, 0, 0);
    ctx.restore();
    ctx.save();
    ctx.beginPath();
    ctx.rect(v.plot.x, v.plot.y, v.plot.w, v.plot.h);
    ctx.clip();
    this.drawVectors(ctx, v);
    this.drawSelection(ctx, v);
    this.drawMeasure(ctx, v);
    this.drawHover(ctx, v);
    ctx.restore();
    if (!this.data.count) {
      ctx.fillStyle = 'rgba(231,236,240,.75)';
      ctx.font = '13px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(this.loading ? 'Henter punkter i korridoren …' : 'Ingen punkter i korridoren', v.plot.x + v.plot.w / 2, v.plot.y + v.plot.h / 2);
      ctx.textAlign = 'start';
    }
  }

  private drawGrid(ctx: CanvasRenderingContext2D, v: ProfileViewport, w: number, h: number) {
    const [s0, z1] = toProfile(v, v.plot.x, v.plot.y), [s1, z0] = toProfile(v, v.plot.x + v.plot.w, v.plot.y + v.plot.h);
    const xs = niceTicks(s0, s1, Math.max(2, Math.floor(v.plot.w / 90))), zs = niceTicks(z0, z1, Math.max(2, Math.floor(v.plot.h / 45)));
    const dec = (step: number) => (step >= 1 ? 0 : step >= 0.1 ? 1 : 2);
    ctx.lineWidth = 1;
    ctx.font = '11px system-ui, sans-serif';
    ctx.strokeStyle = 'rgba(231,236,240,.09)';
    ctx.beginPath();
    for (const s of xs.values) { const x = Math.round(toScreen(v, s, 0)[0]) + 0.5; ctx.moveTo(x, v.plot.y); ctx.lineTo(x, v.plot.y + v.plot.h); }
    for (const z of zs.values) { const y = Math.round(toScreen(v, 0, z)[1]) + 0.5; ctx.moveTo(v.plot.x, y); ctx.lineTo(v.plot.x + v.plot.w, y); }
    ctx.stroke();
    ctx.fillStyle = 'rgba(231,236,240,.72)';
    ctx.textAlign = 'center';
    for (const s of xs.values) { const x = toScreen(v, s, 0)[0]; if (x > v.plot.x - 1 && x < v.plot.x + v.plot.w + 1) ctx.fillText(nb(s, dec(xs.step)), x, h - 8); }
    ctx.textAlign = 'right';
    for (const z of zs.values) { const y = toScreen(v, 0, z)[1]; if (y > v.plot.y - 1 && y < v.plot.y + v.plot.h + 1) ctx.fillText(nb(z, dec(zs.step)), v.plot.x - 6, y + 4); }
    ctx.textAlign = 'start';
    ctx.fillStyle = 'rgba(231,236,240,.55)';
    ctx.fillText(`H (m, ${this.vertical})`, 6, 12);
    const ex = v.sz / v.sx;
    ctx.textAlign = 'right';
    ctx.fillText(`Avstand fra A (m) · overhøyde ${nb(ex, ex < 10 ? 1 : 0)}×`, w - MARGIN.right, 12);
    ctx.textAlign = 'start';
    // A/B-endene
    if (this.section) {
      ctx.setLineDash([4, 4]);
      ctx.strokeStyle = 'rgba(245,158,11,.75)';
      ctx.fillStyle = '#f59e0b';
      ctx.font = 'bold 12px system-ui, sans-serif';
      for (const [s, name] of [[0, 'A'], [profileLength(this.section), 'B']] as const) {
        const x = Math.round(toScreen(v, s, 0)[0]) + 0.5;
        if (x < v.plot.x - 1 || x > v.plot.x + v.plot.w + 1) continue;
        ctx.beginPath(); ctx.moveTo(x, v.plot.y); ctx.lineTo(x, v.plot.y + v.plot.h); ctx.stroke();
        ctx.fillText(name, x + 4, v.plot.y + 13);
      }
      ctx.setLineDash([]);
    }
    ctx.strokeStyle = 'rgba(231,236,240,.25)';
    ctx.strokeRect(v.plot.x + 0.5, v.plot.y + 0.5, v.plot.w - 1, v.plot.h - 1);
  }

  private drawVectors(ctx: CanvasRenderingContext2D, v: ProfileViewport) {
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    for (const vec of this.vectors) {
      ctx.strokeStyle = vec.color;
      ctx.fillStyle = vec.color;
      ctx.globalAlpha = 0.8; // punktene under skal fortsatt synes
      if (vec.type === 'line') {
        ctx.lineWidth = vec.width;
        ctx.setLineDash(vec.dashed ? [6, 4] : []);
        ctx.beginPath();
        for (let i = 0; i < vec.pts.length; i += 2) { const [x, y] = toScreen(v, vec.pts[i], vec.pts[i + 1]); if (i) ctx.lineTo(x, y); else ctx.moveTo(x, y); }
        // et linjestykke som krysser korridoren på tvers blir ett punkt: tegn som prikk
        if (vec.pts.length === 4 && Math.hypot((vec.pts[2] - vec.pts[0]) * v.sx, (vec.pts[3] - vec.pts[1]) * v.sz) < 2) { const [x, y] = toScreen(v, vec.pts[0], vec.pts[1]); ctx.moveTo(x + 3, y); ctx.arc(x, y, 3, 0, Math.PI * 2); ctx.fill(); }
        ctx.stroke();
      } else if (vec.width > 0) {
        const [x, y] = toScreen(v, vec.pts[0], vec.pts[1]);
        ctx.setLineDash([]);
        ctx.beginPath(); ctx.arc(x, y, vec.width / 2, 0, Math.PI * 2); ctx.fill();
        ctx.lineWidth = 1.5; ctx.strokeStyle = '#0d1218'; ctx.stroke();
      }
      ctx.globalAlpha = 1;
      if (vec.label) {
        const [x, y] = toScreen(v, vec.pts[0], vec.pts[1]);
        ctx.font = '600 11px system-ui, sans-serif';
        const w = ctx.measureText(vec.label).width + 8;
        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(15,23,42,.88)'; ctx.fillRect(x - w / 2, y - 22, w, 16);
        ctx.strokeStyle = vec.color; ctx.lineWidth = 1; ctx.strokeRect(x - w / 2 + 0.5, y - 21.5, w - 1, 15);
        ctx.fillStyle = '#f8fafc'; ctx.textAlign = 'center'; ctx.fillText(vec.label, x, y - 10); ctx.textAlign = 'start';
      }
    }
    ctx.setLineDash([]);
  }

  private drawSelection(ctx: CanvasRenderingContext2D, v: ProfileViewport) {
    ctx.strokeStyle = '#ff9900';
    ctx.fillStyle = 'rgba(255,153,0,.12)';
    ctx.lineWidth = 1.5;
    if (this.drag?.mode === 'select' && this.drag.moved) {
      const x = Math.min(this.drag.x0, this.drag.x), y = Math.min(this.drag.y0, this.drag.y), w = Math.abs(this.drag.x - this.drag.x0), h = Math.abs(this.drag.y - this.drag.y0);
      ctx.fillRect(x, y, w, h); ctx.strokeRect(x, y, w, h);
    } else if (this.selection && this.pending?.scope !== 'VISIBLE_POINTS') {
      const a = toScreen(v, this.selection[0][0], this.selection[0][1]), b = toScreen(v, this.selection[2][0], this.selection[2][1]);
      ctx.fillRect(a[0], b[1], b[0] - a[0], a[1] - b[1]); ctx.strokeRect(a[0], b[1], b[0] - a[0], a[1] - b[1]);
    } else if (this.selection) {
      const [x, y] = toScreen(v, (this.selection[0][0] + this.selection[2][0]) / 2, (this.selection[0][1] + this.selection[2][1]) / 2);
      ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2); ctx.stroke();
    }
  }

  private drawMeasure(ctx: CanvasRenderingContext2D, v: ProfileViewport) {
    if (!this.measure.length) return;
    const pts = [...this.measure];
    if (pts.length === 1 && this.hover && this.tool === 'measure') pts.push(this.snap(this.hover));
    const sc = pts.map((p) => toScreen(v, p[0], p[1]));
    ctx.strokeStyle = '#fef08a'; ctx.fillStyle = '#fef08a'; ctx.lineWidth = 1.5;
    if (sc.length === 2) {
      ctx.setLineDash([5, 4]);
      ctx.beginPath(); ctx.moveTo(sc[0][0], sc[0][1]); ctx.lineTo(sc[1][0], sc[0][1]); ctx.lineTo(sc[1][0], sc[1][1]); ctx.stroke();
      ctx.setLineDash([]);
      ctx.beginPath(); ctx.moveTo(sc[0][0], sc[0][1]); ctx.lineTo(sc[1][0], sc[1][1]); ctx.stroke();
      const ds = pts[1][0] - pts[0][0], dz = pts[1][1] - pts[0][1], text = `Δs ${nb(Math.abs(ds), 2)} m · ΔH ${dz >= 0 ? '+' : '−'}${nb(Math.abs(dz), 2)} m · skrå ${nb(Math.hypot(ds, dz), 2)} m`;
      ctx.font = '600 12px system-ui, sans-serif';
      const w = ctx.measureText(text).width + 10, x = Math.min(v.plot.x + v.plot.w - w - 4, Math.max(v.plot.x + 4, (sc[0][0] + sc[1][0]) / 2 - w / 2)), y = Math.max(v.plot.y + 4, Math.min(sc[0][1], sc[1][1]) - 26);
      ctx.fillStyle = 'rgba(15,23,42,.92)'; ctx.fillRect(x, y, w, 19);
      ctx.fillStyle = '#fef08a'; ctx.fillText(text, x + 5, y + 14);
    }
    ctx.fillStyle = '#fef08a';
    for (const p of sc) { ctx.beginPath(); ctx.arc(p[0], p[1], 3.5, 0, Math.PI * 2); ctx.fill(); }
  }

  private drawHover(ctx: CanvasRenderingContext2D, v: ProfileViewport) {
    if (!this.hover || this.drag?.mode === 'pan') return;
    const [x, y] = this.hover;
    ctx.strokeStyle = 'rgba(231,236,240,.35)'; ctx.lineWidth = 1; ctx.setLineDash([2, 3]);
    ctx.beginPath(); ctx.moveTo(Math.round(x) + 0.5, v.plot.y); ctx.lineTo(Math.round(x) + 0.5, v.plot.y + v.plot.h); ctx.moveTo(v.plot.x, Math.round(y) + 0.5); ctx.lineTo(v.plot.x + v.plot.w, Math.round(y) + 0.5); ctx.stroke();
    ctx.setLineDash([]);
    const i = this.nearest(x, y);
    if (i >= 0) {
      const [px, py] = toScreen(v, this.data.s[i], this.data.z[i]);
      ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.arc(px, py, 5, 0, Math.PI * 2); ctx.stroke();
    }
  }

  // ------------------------------------------------------------------ interaksjon
  private local(e: MouseEvent): XY { const r = this.canvas.getBoundingClientRect(); return [e.clientX - r.left, e.clientY - r.top]; }
  private nearest(x: number, y: number, radius = 7): number {
    if (!this.pick || !this.image) return -1;
    const dpr = this.image.width / Math.max(1, this.canvas.clientWidth);
    const i = nearestPicked(this.pick, this.image.width, this.image.height, x * dpr, y * dpr, Math.round(radius * dpr));
    return i < this.data.count ? i : -1;
  }
  /** Profilkoordinat under markøren, festet til nærmeste punkt når det finnes (mål). */
  private snap(p: XY): XY {
    const i = this.nearest(p[0], p[1]);
    return i >= 0 ? [this.data.s[i], this.data.z[i]] : toProfile(this.view!, p[0], p[1]);
  }

  private setTool(t: Tool) {
    if (t === 'select' && !this.classifying) t = 'pan';
    this.tool = t;
    for (const [id, b] of this.toolButtons) { b.classList.toggle('active', id === t); b.setAttribute('aria-pressed', String(id === t)); }
    this.canvas.dataset.tool = t;
    if (t !== 'measure') this.measure = [];
    this.schedule();
  }

  private bindPointer() {
    const c = this.canvas;
    c.addEventListener('contextmenu', (e) => e.preventDefault());
    c.addEventListener('pointerdown', (e) => {
      if (!this.view) return;
      c.focus({ preventScroll: true });
      const [x, y] = this.local(e), pan = e.button === 1 || e.button === 2 || (e.button === 0 && this.tool === 'pan');
      if (e.button !== 0 && !pan) return;
      c.setPointerCapture(e.pointerId);
      this.drag = { id: e.pointerId, x, y, x0: x, y0: y, mode: pan ? 'pan' : 'select', moved: false };
      if (pan) c.classList.add('panning');
    });
    c.addEventListener('pointermove', (e) => {
      const [x, y] = this.local(e);
      this.hover = [x, y];
      const d = this.drag;
      if (d && d.id === e.pointerId) {
        if (!d.moved && Math.hypot(x - d.x0, y - d.y0) > 3) d.moved = true;
        if (d.mode === 'pan' && d.moved) { this.view = panViewport(this.view!, x - d.x, y - d.y); this.userMoved = true; this.interacting(); }
        d.x = x; d.y = y;
      }
      this.updateReadout();
      this.schedule();
    });
    const end = (e: PointerEvent) => {
      const d = this.drag;
      if (!d || d.id !== e.pointerId) return;
      this.drag = null;
      c.classList.remove('panning');
      const [x, y] = this.local(e);
      if (d.mode === 'pan') {
        if (!d.moved && this.tool === 'measure' && e.button === 0) this.measureClick(x, y);
        this.schedule();
        return;
      }
      if (this.tool === 'measure') { this.measureClick(x, y); return; }
      if (this.tool === 'select') {
        if (d.moved) this.selectRect(d.x0, d.y0, x, y);
        else this.selectPoint(x, y);
      }
      this.schedule();
    };
    c.addEventListener('pointerup', end);
    c.addEventListener('pointercancel', (e) => { if (this.drag?.id === e.pointerId) { this.drag = null; c.classList.remove('panning'); this.schedule(); } });
    c.addEventListener('pointerleave', () => { this.hover = null; this.readout.textContent = ''; this.schedule(); });
    c.addEventListener('wheel', (e) => {
      if (!this.view) return;
      e.preventDefault();
      const [x, y] = this.local(e), px = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      const factor = Math.exp(-px * (e.ctrlKey ? 0.01 : 0.0015));
      this.view = zoomViewport(this.view, x, y, factor, e.shiftKey ? 'z' : e.altKey ? 's' : 'both');
      this.userMoved = true;
      this.interacting();
      this.updateReadout();
      this.schedule();
    }, { passive: false });
    c.addEventListener('dblclick', () => { this.userMoved = false; this.fit(); });
    c.addEventListener('keydown', (e) => {
      if (!this.view) return;
      const k = e.key.toLowerCase(), cx = this.view.plot.x + this.view.plot.w / 2, cy = this.view.plot.y + this.view.plot.h / 2;
      if (k === 'f' || k === 'home') { this.userMoved = false; this.fit(); }
      else if (k === '+' || k === '=') this.view = zoomViewport(this.view, cx, cy, 1.25);
      else if (k === '-') this.view = zoomViewport(this.view, cx, cy, 0.8);
      else if (k === 'arrowleft') this.view = panViewport(this.view, 60, 0);
      else if (k === 'arrowright') this.view = panViewport(this.view, -60, 0);
      else if (k === 'arrowup') this.view = panViewport(this.view, 0, 40);
      else if (k === 'arrowdown') this.view = panViewport(this.view, 0, -40);
      else if (k === 'escape') { if (this.measure.length || this.selection) { this.measure = []; this.invalidate(); } else return; }
      else if (k === 'm') this.setTool('measure');
      else if (k === 'p') this.setTool('pan');
      else return;
      e.preventDefault();
      e.stopPropagation();
      if (k !== 'f' && k !== 'home' && k !== 'escape') this.userMoved = true;
      this.schedule();
    });
  }

  private updateReadout() {
    if (!this.hover || !this.view || !this.section) return;
    const [x, y] = this.hover, v = this.view;
    if (x < v.plot.x || x > v.plot.x + v.plot.w || y < v.plot.y || y > v.plot.y + v.plot.h) { this.readout.textContent = ''; return; }
    const [s, z] = toProfile(v, x, y), parts = [`s ${nb(s, 2)} m`, `H ${nb(z, 2)} m`];
    const i = this.nearest(x, y);
    if (i >= 0) {
      const c = this.data.classes[i], src = this.data.source[i], [e, n] = this.data.xy(this.section, i);
      parts.push(`punkt: ${c} ${classInfo(c).name}${c !== src ? ` (kilde ${src})` : ''} · H ${nb(this.data.z[i], 2)} m · ${nb(this.data.t[i], 2)} m fra midtlinjen · E ${nb(e, 2)} N ${nb(n, 2)}`);
    }
    this.readout.textContent = parts.join(' · ');
  }

  private measureClick(x: number, y: number) {
    const p = this.snap([x, y]);
    if (this.measure.length >= 2) this.measure = [];
    this.measure.push(p);
    this.schedule();
  }

  private shift(direction: number) {
    const p = this.section;
    if (!p) return;
    const L = profileLength(p), nx = -(p.b[1] - p.a[1]) / L * p.width * direction, ny = (p.b[0] - p.a[0]) / L * p.width * direction;
    this.open({ ...p, a: [p.a[0] + nx, p.a[1] + ny], b: [p.b[0] + nx, p.b[1] + ny], selection: [] }, false);
  }
  private reverse() { const p = this.section; if (p) this.open({ ...p, a: p.b, b: p.a, selection: [] }, true); }

  // ------------------------------------------------------------------ 3D-korridor
  private clearCorridor() {
    for (const child of [...this.group.children]) {
      this.group.remove(child);
      const o = child as THREE.Mesh;
      o.geometry?.dispose();
      const m = o.material as THREE.Material & { map?: THREE.Texture | null };
      m?.map?.dispose();
      m?.dispose();
    }
    this.engine.requestRender();
  }

  /** Korridoren som en boks i 3D (bunn/topp fra profilens punkter) med A/B-merker. */
  private drawCorridor() {
    this.clearCorridor();
    const p = this.section, o = this.engine.origin;
    if (!p || !o) return;
    const zr = robustRange(this.data.z.subarray(0, this.data.count), 0.001, 0.999) ?? (() => { const t = this.engine.targetAbsolute()?.[2] ?? o[2]; return [t - 5, t + 25] as [number, number]; })();
    const ring = profileRing(p).map(([x, y]) => [x - o[0], y - o[1]]), z0 = zr[0] - o[2] - 0.5, z1 = zr[1] - o[2] + 0.5, pos: number[] = [];
    for (let i = 0; i < 4; i++) {
      const a = ring[i], b = ring[(i + 1) % 4];
      pos.push(a[0], a[1], z0, b[0], b[1], z0, a[0], a[1], z1, b[0], b[1], z1, a[0], a[1], z0, a[0], a[1], z1);
    }
    const g = new THREE.BufferGeometry().setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    const lines = new THREE.LineSegments(g, new THREE.LineBasicMaterial({ color: '#f59e0b', depthTest: false, transparent: true, opacity: 0.9 }));
    lines.renderOrder = 45;
    lines.frustumCulled = false;
    this.group.add(lines);
    for (const [name, xy] of [['A', p.a], ['B', p.b]] as [string, XY][]) {
      const canvas = document.createElement('canvas');
      canvas.width = 64; canvas.height = 64;
      const c2 = canvas.getContext('2d')!;
      c2.fillStyle = '#f59e0b'; c2.beginPath(); c2.arc(32, 32, 30, 0, Math.PI * 2); c2.fill();
      c2.fillStyle = '#101b27'; c2.font = 'bold 38px sans-serif'; c2.textAlign = 'center'; c2.fillText(name, 32, 46);
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: new THREE.CanvasTexture(canvas), depthTest: false, sizeAttenuation: false }));
      sprite.position.set(xy[0] - o[0], xy[1] - o[1], z1);
      sprite.scale.set(0.035, 0.035, 1);
      sprite.renderOrder = 46;
      this.group.add(sprite);
    }
    this.engine.requestRender();
  }

  // ------------------------------------------------------------------ klassifisering i profilen
  private buildClassifyPanel(): HTMLElement {
    const preset = (name: string, codes: number[]) => el('button', { type: 'button', onclick: () => { for (const option of this.sources.options) option.selected = codes.includes(Number(option.value)); this.invalidate(); } }, name);
    const endpoints = el('details', {}, el('summary', {}, 'Juster A / B (prosjektets CRS)'));
    for (const endpoint of ['a', 'b'] as const) for (const [axis, label] of [[0, 'E'], [1, 'N']] as const) {
      const input = el('input', { type: 'number', step: 0.1, 'aria-label': `Profil ${endpoint.toUpperCase()} ${label}` });
      input.addEventListener('focus', () => { if (this.section) input.value = String(this.section[endpoint][axis]); });
      input.addEventListener('change', () => {
        if (!this.section || !Number.isFinite(Number(input.value))) return;
        const p = structuredClone(this.section);
        p[endpoint][axis] = Number(input.value);
        if (profileLength(p) < 0.1) { this.info.textContent = 'A og B må være ulike.'; return; }
        this.open(p);
      });
      endpoints.append(el('label', {}, `${endpoint.toUpperCase()} ${label}`, input));
    }
    for (const input of [this.sources, this.target, this.reason]) input.addEventListener('input', () => { if (this.pending) this.preview(); });
    this.apply.onclick = () => this.applyEdit();
    const panel = el('aside', { class: 'profile-classify hidden', 'aria-label': 'Klassifiser i profilen' },
      el('div', { class: 'note' }, 'Velg-verktøyet: dra et rektangel eller klikk et punkt.'),
      el('label', {}, 'Kildeklasser', this.sources),
      el('div', { class: 'profile-presets' }, preset('Vegetasjon', [3, 4, 5]), preset('Ukl. + veg. + ledning', [1, 3, 4, 5, 13, 14])),
      el('label', {}, 'Sett klasse', this.target),
      el('label', {}, 'Begrunnelse', this.reason),
      el('button', { type: 'button', onclick: () => this.preview() }, 'Forhåndsvis utvalg'), this.apply,
      el('div', { class: 'profile-presets' },
        el('button', { type: 'button', onclick: () => this.undo(false) }, 'Angre'), el('button', { type: 'button', onclick: () => this.undo(true) }, 'Gjør om')),
      el('div', { class: 'profile-presets' },
        el('button', { type: 'button', title: 'Vis bare kildeklassene (i profil og 3D)', onclick: () => { const shown = this.codes(); this.engine.setHiddenClasses(new Set(Array.from({ length: 256 }, (_, i) => i).filter((i) => !shown.includes(i)))); this.invalidate(); } }, 'Isoler klasser'),
        el('button', { type: 'button', onclick: () => { this.engine.setHiddenClasses(new Set()); this.invalidate(); } }, 'Vis alle')),
      endpoints, this.info);
    return panel;
  }

  private codes() { return Array.from(this.sources.selectedOptions, (o) => Number(o.value)); }

  private invalidate() {
    this.pending = null;
    this.selection = null;
    this.selMask = null;
    this.selVersion++;
    this.apply.disabled = true;
    if (this.session.selection) this.session.select(null);
    this.schedule();
  }

  private undo(redo: boolean) {
    this.invalidate();
    if (redo) this.session.redo(); else this.session.undo();
    this.session.saveLocal();
    void this.session.saveServer().catch(() => {});
    this.hooks.redraw();
    this.schedule();
  }

  private baseEdit(): ClassEdit {
    if (!this.section || this.session.originalView) throw new Error('Åpne profil og bruk Korrigert visning.');
    if (!this.codes().length) throw new Error('Velg kildeklasser.');
    const layers = [...new Set(this.data.nodes.map((n) => n.layer))].filter((id) => this.engine.layer(id)?.header);
    if (!layers.length) throw new Error('Ingen punktsky i profilen.');
    return { id: crypto.randomUUID(), createdAt: new Date().toISOString(), reason: this.reason.value, reviewer: 'Anonym lenkebruker', layers, ring: profileRing(this.section), profile: { ...structuredClone(this.section), selection: this.selection ?? [] }, sourceClasses: this.codes(), target: Number(this.target.value), status: 'MANUAL_NOT_VALIDATED', scope: 'VOLUME', selectionMethod: 'PROFILE_RECTANGLE' };
  }

  private selectRect(x0: number, y0: number, x1: number, y1: number) {
    if (!this.view) return;
    const p = toProfile(this.view, x0, y0), q = toProfile(this.view, x1, y1);
    this.invalidate();
    this.selection = [[Math.min(p[0], q[0]), Math.min(p[1], q[1])], [Math.max(p[0], q[0]), Math.min(p[1], q[1])], [Math.max(p[0], q[0]), Math.max(p[1], q[1])], [Math.min(p[0], q[0]), Math.max(p[1], q[1])]];
    this.preview();
  }

  private selectPoint(x: number, y: number) {
    const i = this.nearest(x, y, 8);
    this.invalidate();
    if (i < 0) { this.info.textContent = 'Ingen synlig profilpunkt her.'; return; }
    const ref = this.data.nodes[this.data.node[i]], s = this.data.s[i], z = this.data.z[i];
    this.selection = [[s - 0.1, z - 0.1], [s + 0.1, z - 0.1], [s + 0.1, z + 0.1], [s - 0.1, z + 0.1]];
    try {
      const edit = this.baseEdit(), bits = new Uint8Array(Math.ceil(ref.nodePoints / 8)), k = this.data.index[i];
      bits[k >> 3] |= 1 << (k & 7);
      edit.layers = [ref.layer];
      edit.scope = 'VISIBLE_POINTS';
      edit.selectionMethod = 'PROFILE_POINT';
      edit.nodes = [{ layer: ref.layer, key: ref.key, pointCount: ref.nodePoints, bits: encodeBits(bits), selectedCount: 1 }];
      edit.selectedCount = 1;
      this.pending = edit;
      this.preview();
    } catch (e) { this.info.textContent = (e as Error).message; }
  }

  private preview() {
    try {
      if (!this.section || (!this.selection && !this.pending)) throw new Error('Velg punkter i profilen først (Velg-verktøyet).');
      const edit = this.pending?.scope === 'VISIBLE_POINTS' ? { ...this.pending, sourceClasses: this.codes(), target: Number(this.target.value), reason: this.reason.value } : this.baseEdit();
      const res = selectionMask(this.data, this.section, edit);
      this.selMask = res.mask;
      this.selVersion++;
      if (!res.eligible) throw new Error(res.selected ? `${count(res.selected)} punkter valgt, men ingen i kildeklassene.` : 'Ingen punkter i utvalget.');
      edit.selectedCount = res.eligible;
      this.pending = edit;
      this.session.select(edit);
      this.apply.disabled = false;
      this.info.textContent = `${count(res.eligible)} profilpunkter i kildeklassene (${count(res.changing)} endres) → ${edit.target} ${classInfo(edit.target).name}. ${edit.scope === 'VOLUME' ? 'Regelen gjelder hele korridorvolumet, også punkter som ikke er lastet, ved publisering.' : 'Ett stabilt kildepunkt valgt.'}`;
    } catch (e) {
      this.info.textContent = (e as Error).message;
      this.apply.disabled = true;
    }
    this.schedule();
  }

  private applyEdit() {
    try {
      if (!this.pending) throw new Error('Forhåndsvis først.');
      this.session.add(this.pending);
      const hidden = new Set(this.engine.style.hiddenClasses);
      hidden.delete(this.pending.target);
      this.engine.setHiddenClasses(hidden);
      this.session.saveLocal();
      void this.session.saveServer().catch(() => {});
      const target = this.pending.target;
      this.invalidate();
      this.info.textContent = `Korreksjon lagt til → ${target} ${classInfo(target).name}. Angre er mulig.`;
      this.hooks.redraw();
    } catch (e) { this.info.textContent = (e as Error).message; }
  }
}
